/**
 * FeatureList Component
 * Displays a list of features with check marks
 */
import { cn } from '@/utils/cn';
import type { PlanFeature } from '@/types/billing';

export interface FeatureListProps {
  features: PlanFeature[];
  variant?: 'default' | 'compact';
  className?: string;
}

/**
 * Check icon for included features
 */
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn('w-4 h-4 text-green-500 flex-shrink-0', className)}
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * X icon for excluded features
 */
function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn('w-4 h-4 text-gray-300 flex-shrink-0', className)}
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Format limit display for features with limits
 */
function formatLimit(limit?: number): string {
  if (limit === undefined || limit === null) {
    return '';
  }
  if (limit === -1) {
    return 'Unlimited';
  }
  return `Up to ${limit}`;
}

export default function FeatureList({
  features,
  variant = 'default',
  className,
}: FeatureListProps) {
  const spacing = variant === 'compact' ? 'space-y-1.5' : 'space-y-2';
  const textSize = variant === 'compact' ? 'text-xs' : 'text-sm';

  return (
    <ul className={cn(spacing, className)} role="list">
      {features.map((feature) => (
        <li
          key={feature.key}
          className={cn('flex items-start gap-2', textSize)}
        >
          {feature.included ? <CheckIcon /> : <XIcon />}
          <span
            className={cn(
              feature.included ? 'text-gray-700' : 'text-gray-400'
            )}
          >
            {feature.label}
            {feature.limit !== undefined && feature.included && (
              <span className="text-gray-500 ml-1">
                ({formatLimit(feature.limit)})
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
